import axios from 'axios';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { tokenStorage } from './tokenStorage';
import { Sentry } from './sentry';
import { APP_VERSION } from '@/config/version';
import { getCurrentLanguageCode } from '@/composables/useLanguageSettings';

// Network retry configuration
const NETWORK_RETRY_DELAY = 5000;
const NETWORK_MAX_RETRIES = 3;

// Use different base URLs for web (proxy) vs native (direct)
const APP_PLATFORM = Capacitor.getPlatform() === 'web' ? 'android' : Capacitor.getPlatform();
const API_BASE_URL = Capacitor.isNativePlatform()
  ? 'https://www.protipster.com'  // Native: Direct API URL
  : '';  // Web: Empty string, Vite proxy handles routing

const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'X-App-Platform': APP_PLATFORM,
  'X-App-Version': APP_VERSION,
};

const isNetworkError = (error) => {
  if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
    return true;
  }

  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return true;
  }

  if (error.response?.status === 0) {
    return true;
  }

  return !error.response && error.request;
};

const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

/**
 * Handle unexpected authentication failure (token refresh failed)
 * Captures exception to Sentry, clears tokens, and redirects to login
 */
const handleUnexpectedAuthFailure = async (error, context = {}) => {
  const endpoint = error.config?.url || context.url;

  Sentry.captureException(error, {
    tags: { type: 'unexpected_session_failure' },
    extra: {
      endpoint,
      status: error.response?.status || context.status,
      message: error.message,
    },
  });

  console.error('🔒 Session failure:', {
    endpoint,
    status: error.response?.status || context.status,
    message: error.message,
  });

  await tokenStorage.clearTokens();

  window.location.href = '/auth';
};

const withNetworkRetry = async (fn) => {
  let lastError;

  for (let attempt = 0; attempt <= NETWORK_MAX_RETRIES; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (!isNetworkError(error) || attempt === NETWORK_MAX_RETRIES) {
        throw error;
      }

      console.warn(`🔄 Network error, retrying in ${NETWORK_RETRY_DELAY / 1000}s (attempt ${attempt + 1}/${NETWORK_MAX_RETRIES})...`);

      await delay(NETWORK_RETRY_DELAY);
    }
  }

  throw lastError;
};

// Shared token refresh lock — prevents concurrent refresh attempts
let isRefreshing = false;
let refreshQueue = [];

const processRefreshQueue = (error, token = null) => {
  refreshQueue.forEach((pending) => {
    if (error) {
      pending.reject(error);
    } else {
      pending.resolve(token);
    }
  });
  refreshQueue = [];
};

const refreshTokenWithLock = async () => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      refreshQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    const storedRefreshToken = await tokenStorage.getRefreshToken();

    if (!storedRefreshToken) {
      throw new Error('No refresh token available');
    }

    const { refreshToken } = await import('./auth.js');
    const response = await refreshToken(storedRefreshToken);

    await tokenStorage.saveTokens(response.token, response.refreshToken);

    console.log('✅ Session renewed successfully');

    processRefreshQueue(null, response.token);

    return response.token;
  } catch (refreshError) {
    processRefreshQueue(refreshError, null);

    throw refreshError;
  } finally {
    isRefreshing = false;
  }
};

const buildNativeHeaders = (token, url, configHeaders = {}) => {
  const isRefreshEndpoint = url.includes('/api/auth/refresh');

  return {
    ...DEFAULT_HEADERS,
    ...(token && !isRefreshEndpoint && { Authorization: `Bearer ${token}` }),
    ...configHeaders,
  };
};

const buildFullUrl = (url, params) => {
  let fullUrl = `${API_BASE_URL}${url}`;

  if (params) {
    const queryString = new URLSearchParams(params).toString();

    if (queryString) {
      fullUrl += `?${queryString}`;
    }
  }

  return fullUrl;
};

const addLanguageParameter = (params) => {
  if (!params) {
    params = new URLSearchParams();
  }

  if (params instanceof URLSearchParams) {
    params.append('lang', getCurrentLanguageCode());

    return params;
  }

  return { ...params, lang: getCurrentLanguageCode() };
}

const parseNativeResponse = (response) => {
  let parsedData = response.data;

  if (typeof response.data === 'string' && response.data.length > 0) {
    try {
      parsedData = JSON.parse(response.data);
    } catch (e) {
      console.warn('⚠️ Failed to parse response data as JSON:', e);
    }
  }

  return { data: parsedData, status: response.status, headers: response.headers };
};

// Helper to handle 401 errors and token refresh for native platform
const handleNativeApiCall = async (apiCall, url, retryCount = 0) => {
  try {
    const response = await apiCall();

    // CapacitorHttp returns status 0 when device is offline instead of throwing
    if (response.status === 0) {
      const networkError = new Error('Network Error');

      networkError.code = 'ERR_NETWORK';

      throw networkError;
    }

    // IMPORTANT: Don't try to refresh if this IS the refresh endpoint
    // This prevents infinite loops when refresh token itself is expired
    const isRefreshEndpoint = url.includes('/api/auth/refresh');

    // Check for 401 status
    if (response.status === 401 && retryCount === 0 && !isRefreshEndpoint) {
      console.log('⚠️ CapacitorHttp 401 detected, attempting session renewal...');

      try {
        await refreshTokenWithLock();

        // Retry the original request with a new token (only once)
        return await handleNativeApiCall(apiCall, url, retryCount + 1);
      } catch (refreshError) {
        console.error('❌ Session renewal failed:', refreshError.message);

        Sentry.setContext('session_renewal_failure', {
          endpoint: url,
          retry_attempt: retryCount,
          error_message: refreshError.message,
        });

        if (refreshError.response?.status === 401) {
          await handleUnexpectedAuthFailure(refreshError, { url });
        }

        throw refreshError;
      }
    }

    // If this is the refresh endpoint returning 401, or already retried, throw to prevent saving undefined tokens
    if (response.status === 401) {
      console.log('🔒 Session endpoint returned 401 or retry exhausted, clearing credentials...');

      Sentry.setContext('session_expired', {
        endpoint: url,
        retry_attempt: retryCount,
        is_renewal_endpoint: isRefreshEndpoint,
        response_code: 401,
      });

      const authError = new Error('Session renewal failed: 401');
      authError.response = { status: 401 };

      await handleUnexpectedAuthFailure(authError, { url, status: 401 });

      throw authError;
    }

    return response;
  } catch (error) {
    console.error('❌ CapacitorHttp Error:', error);
    Sentry.captureException(error);

    throw error;
  }
};

// Create a wrapper that uses CapacitorHttp on native, axios on web
const createApiClient = () => {
  if (Capacitor.isNativePlatform()) {
    // Native platform: Use CapacitorHttp with 401 handling
    return {
      async get(url, config = {}) {
        const apiCall = async () => {
          const token = await tokenStorage.getAccessToken();
          const headers = buildNativeHeaders(token, url, config.headers);
          const params = addLanguageParameter(config.params);
          const fullUrl = buildFullUrl(url, params);

          console.log('🚀 CapacitorHttp GET:', {
            url: fullUrl,
            hasSession: !!token,
          });

          const response = await CapacitorHttp.get({ url: fullUrl, headers });

          console.log('✅ CapacitorHttp Response:', {
            url: fullUrl,
            status: response.status,
          });

          return parseNativeResponse(response);
        };

        return await withNetworkRetry(() => handleNativeApiCall(apiCall, url));
      },

      async post(url, data, config = {}) {
        const apiCall = async () => {
          const token = await tokenStorage.getAccessToken();
          const headers = buildNativeHeaders(token, url, config.headers);
          const fullUrl = buildFullUrl(url);

          console.log('🚀 CapacitorHttp POST:', {
            url: fullUrl,
            hasSession: !!token,
            hasData: !!data,
          });

          const response = await CapacitorHttp.post({ url: fullUrl, headers, data });

          console.log('✅ CapacitorHttp Response:', {
            url: fullUrl,
            status: response.status,
          });

          return parseNativeResponse(response);
        };

        return await withNetworkRetry(() => handleNativeApiCall(apiCall, url));
      },

      async put(url, data, config = {}) {
        const apiCall = async () => {
          const token = await tokenStorage.getAccessToken();
          const headers = buildNativeHeaders(token, url, config.headers);
          const fullUrl = buildFullUrl(url);

          console.log('🚀 CapacitorHttp PUT:', {
            url: fullUrl,
            hasSession: !!token,
            hasData: !!data,
          });

          const response = await CapacitorHttp.put({ url: fullUrl, headers, data });

          console.log('✅ CapacitorHttp Response:', {
            url: fullUrl,
            status: response.status,
          });

          return parseNativeResponse(response);
        };

        return await withNetworkRetry(() => handleNativeApiCall(apiCall, url));
      },
    };
  } else {
    // Web platform: Use axios with interceptors
    return axios.create({
      baseURL: API_BASE_URL,
      headers: { ...DEFAULT_HEADERS },
    });
  }
};

const api = createApiClient();

// Only add interceptors for web (axios)
if (!Capacitor.isNativePlatform() && api.interceptors) {
  // Request interceptor: Attach access token and lang to all requests
  api.interceptors.request.use(
  async (config) => {
    // Don't attach Authorization header for refresh endpoint
    const isRefreshEndpoint = config.url?.includes('/api/auth/refresh');
    const token = await tokenStorage.getAccessToken();

    if (token && !isRefreshEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add lang parameter to all GET requests
    if (config.method === 'get') {
      config.params = addLanguageParameter(config.params);
    }

    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    Sentry.captureException(error);

    return Promise.reject(error);
  }
);

// Response interceptor: Handle 401 errors and auto-refresh tokens
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    console.error('❌ API Error Response:', {
      message: error.message,
      code: error.code,
      endpoint: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      statusText: error.response?.statusText,
      isNetworkError: isNetworkError(error),
    });
    Sentry.captureException(error);

    const originalRequest = error.config;

    // Retry on network errors (no response from server)
    if (isNetworkError(error) && !originalRequest._networkRetried) {
      originalRequest._networkRetried = true;

      for (let attempt = 1; attempt <= NETWORK_MAX_RETRIES; attempt++) {
        console.warn(`🔄 Network error, retrying in ${NETWORK_RETRY_DELAY / 1000}s (attempt ${attempt}/${NETWORK_MAX_RETRIES})...`);

        await delay(NETWORK_RETRY_DELAY);

        try {
          return await api(originalRequest);
        } catch (retryError) {
          if (!isNetworkError(retryError) || attempt === NETWORK_MAX_RETRIES) {
            return Promise.reject(retryError);
          }
        }
      }
    }

    // IMPORTANT: Don't try to refresh if this IS the refresh endpoint
    // This prevents infinite loops when refresh token itself is expired
    const isRefreshEndpoint = originalRequest?.url?.includes('/api/auth/refresh');

    // Only handle 401 errors that haven't been retried yet and are NOT from refresh endpoint
    if (error.response?.status === 401 && !originalRequest._authRetry && !isRefreshEndpoint) {
      originalRequest._authRetry = true;

      try {
        const newToken = await refreshTokenWithLock();

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        if (refreshError.response?.status === 401) {
          await handleUnexpectedAuthFailure(refreshError);
        }

        return Promise.reject(refreshError);
      }
    } else if (error.response?.status === 401 && isRefreshEndpoint) {
      console.log('🔒 Session renewal endpoint returned 401');

      Sentry.setContext('session_expired', {
        endpoint: originalRequest?.url,
        response_code: 401,
      });

      await handleUnexpectedAuthFailure(error);
    }

    return Promise.reject(error);
  });
}

export default api;
